// @flow
import { createDuck } from '@rover/react-lib/lib/redux-duck';
import type { CreditCardSummary, CreditCardAddSummary } from '@rover/react-lib/types';

import {
  getCards,
  getCard,
  setDefaultCard,
  removeCard,
  saveCard,
} from '@rover/react-lib/apis/creditCards.api';

import type {
  ActionCreator,
  EffectCreator,
  SelectorCreator,
  DuckOptions,
  Duck,
} from '@rover/react-lib/lib/redux-duck';

export type State = {
  cards: Array<CreditCardSummary>,
  errorMessage: string,
  loading: boolean,
};

type Actions = {
  creditCardsPending: ActionCreator<void>,
  creditCardsFailure: ActionCreator<{ errorMessage: string }>,
  fetchCreditCardsSuccess: ActionCreator<{ cards: Array<CreditCardSummary> }>,
  fetchCreditCardSuccess: ActionCreator<{ creditCard: CreditCardSummary }>,
  setDefaultCreditCardSuccess: ActionCreator<{ creditCard: CreditCardSummary }>,
  removeCreditCardSuccess: ActionCreator<{ cardUrl: string }>,
  saveCreditCardSuccess: ActionCreator<void>,
};

type Effects = {
  fetchCreditCards: EffectCreator<void>,
  fetchCreditCard: EffectCreator<string>,
  setDefaultCreditCard: EffectCreator<string>,
  removeCreditCard: EffectCreator<string>,
  saveCreditCard: EffectCreator<CreditCardAddSummary>,
};

type Selectors = {
  getCreditCards: SelectorCreator<Array<CreditCardSummary>>,
  getDefaultCreditCard: SelectorCreator<CreditCardSummary>,
  getDefaultCreditCardId: SelectorCreator<string>,
  getErrorMessage: SelectorCreator<string>,
  getLoading: SelectorCreator<boolean>,
};

type Options = DuckOptions<State, Actions, Effects, Selectors>;

export const options: Options = {
  name: 'creditCards',
  initialState: {
    cards: [],
    errorMessage: '',
    loading: false,
  },
  actions: {
    creditCardsPending: () => (state): State => ({
      ...state,
      loading: true,
      errorMessage: '',
    }),
    creditCardsFailure: ({ errorMessage }) => (state): State => ({
      ...state,
      loading: false,
      errorMessage,
    }),
    fetchCreditCardsSuccess: ({ cards }) => (state): State => ({
      ...state,
      cards,
      errorMessage: '',
      loading: false,
    }),
    fetchCreditCardSuccess: ({ creditCard }) => (state): State => ({
      ...state,
      cards: [...state.cards.filter(c => c.url !== creditCard.url), creditCard],
      errorMessage: '',
      loading: false,
    }),
    setDefaultCreditCardSuccess: ({ creditCard }) => (state): State => ({
      ...state,
      cards: [
        ...state.cards.map(c => ({
          ...c,
          defaultPayment: c.url === creditCard.url,
        })),
      ],
      errorMessage: '',
      loading: false,
    }),
    removeCreditCardSuccess: ({ cardUrl }) => (state): State => ({
      ...state,
      cards: state.cards.filter(card => card.url !== cardUrl),
      errorMessage: '',
      loading: false,
    }),
    saveCreditCardSuccess: () => (state): State => ({
      ...state,
      errorMessage: '',
      loading: false,
    }),
  },
  effects: {
    fetchCreditCards: () => async (
      dispatch,
      getState,
      { creditCardsPending, creditCardsFailure, fetchCreditCardsSuccess }
    ): Promise<boolean> => {
      dispatch(creditCardsPending());

      try {
        const cards = await getCards();
        dispatch(fetchCreditCardsSuccess({ cards }));
      } catch (error) {
        dispatch(creditCardsFailure({ errorMessage: error.message }));
        return false;
      }
      return true;
    },
    fetchCreditCard: (cardUrl: string) => async (
      dispatch,
      getState,
      { creditCardsPending, creditCardsFailure, fetchCreditCardSuccess }
    ): Promise<boolean> => {
      dispatch(creditCardsPending());

      try {
        const creditCard = await getCard(cardUrl);
        dispatch(fetchCreditCardSuccess({ creditCard }));
      } catch (error) {
        dispatch(creditCardsFailure({ errorMessage: error.message }));
        return false;
      }
      return true;
    },
    setDefaultCreditCard: (cardUrl: string) => async (
      dispatch,
      getState,
      { creditCardsPending, creditCardsFailure, setDefaultCreditCardSuccess }
    ): Promise<boolean> => {
      dispatch(creditCardsPending());

      try {
        const creditCard = await setDefaultCard(cardUrl);
        dispatch(setDefaultCreditCardSuccess({ creditCard }));
      } catch (error) {
        dispatch(creditCardsFailure({ errorMessage: error.message }));
        return false;
      }
      return true;
    },
    removeCreditCard: (cardUrl: string) => async (
      dispatch,
      getState,
      { creditCardsPending, creditCardsFailure, removeCreditCardSuccess },
      { fetchCreditCards }
    ): Promise<void> => {
      dispatch(creditCardsPending());

      try {
        await removeCard(cardUrl);
        dispatch(removeCreditCardSuccess({ cardUrl }));
        dispatch(fetchCreditCards());
      } catch (error) {
        dispatch(creditCardsFailure({ errorMessage: error.message }));
      }
    },
    saveCreditCard: data => async (
      dispatch,
      getState,
      { creditCardsPending, creditCardsFailure, saveCreditCardSuccess },
      { fetchCreditCards },
      { getLoading }
    ): Promise<boolean> => {
      // prevent submission if one is already active
      if (getLoading(getState())) return false;

      dispatch(creditCardsPending());

      try {
        await saveCard(data);
        await dispatch(fetchCreditCards());
        dispatch(saveCreditCardSuccess());
      } catch (error) {
        dispatch(creditCardsFailure({ errorMessage: error.message }));
        return false;
      }
      return true;
    },
  },
  selectors: {
    getCreditCards: (getState, createSelector) =>
      createSelector([getState], s => {
        const NOW = new Date();
        const curMonth = NOW.getMonth() + 1;
        const curYear = NOW.getFullYear();
        return s.cards.filter(card => {
          const cardYear = parseInt(card.expirationYear, 10);
          const cardMonth = parseInt(card.expirationMonth, 10);
          if (cardYear < curYear) {
            return false;
          }
          if (cardYear === curYear && cardMonth < curMonth) {
            return false;
          }

          return true;
        });
      }),

    getDefaultCreditCard: (getState, createSelector, { getCreditCards }) =>
      createSelector([getCreditCards], cards => cards.find(c => c.defaultPayment) || cards[0]),

    getDefaultCreditCardId: (getState, createSelector, { getDefaultCreditCard }) =>
      createSelector([getDefaultCreditCard], defaultCard => (defaultCard ? defaultCard.url : '')),

    getErrorMessage: (getState, createSelector) => createSelector([getState], s => s.errorMessage),

    getLoading: (getState, createSelector) => createSelector([getState], s => s.loading),
  },
};

export const { actions, selectors, effects, reducer } = (createDuck(options): Duck<
  State,
  Actions,
  Effects,
  Selectors
>);



// WEBPACK FOOTER //
// ../react-lib/src/redux/ducks/creditCards.duck.js